import React from 'react';
import { Grid, Tooltip } from '@mui/material';

import { GridListItem, type ItemLayoutProps } from 'app/main/components/GridList';
import { ButtonLink, Icon, ImageAvatar, SquareChip, TagsArray, Text, useSettings } from 'app/main/components';
import { CurrencySquareChip, DateRangeSquareChip, DurationSquareChip } from 'app/main/components/list-items';
import buttons from './ProjectsButtons';

export default function ProjectsListItem(props: ItemLayoutProps) {
	const { sysSettings, t } = useSettings();

	const canShowFinancialStats = sysSettings.projects_show_financial_stats === 'true';

	const avatar = (
		<ImageAvatar
			dimensions={44}
			module="companies"
			uid={props.model.proj_comp_uid}
			avatar={props.model.comp_avatar}
			initials={props.model.proj_title_initials}
			labelBadge={props.model.prst_title_initials}
			labelBadgeTooltip={props.model.prst_title}
			badgeColor={props.model.prst_color}
		/>
	);

	return (
		<GridListItem avatar={avatar} buttons={buttons.view?.itemButtons}>
			<Grid container justifyContent="space-between" spacing={1}>
				<Grid size={props.width >= 960 ? (canShowFinancialStats ? 6 : 8) : props.width >= 600 ? 6 : 12}>
					<Grid container>
						<Grid size={12} className="inline-flex items-center">
							{/* WARNING */}
							{props.model.proj_warning_desc && (
								<Tooltip title={props.model.proj_warning_desc} arrow>
									<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
								</Tooltip>
							)}
							{/* BLOCKED */}
							{props.model.proj_blocked_desc && (
								<Tooltip title={props.model.proj_blocked_desc} arrow>
									<Icon className="mr-2" icon="block" color="error.main" />
								</Tooltip>
							)}
							{/* TITLE */}
							<Text
								className="font-600"
								variant="subtitle2"
								to={{ module: 'projects', uid: props.model.proj_uid }}
							>
								{props.model.proj_title}
								{props.model.comp_title && ` - ${props.model.comp_title}`}
							</Text>
						</Grid>
						{/* TYPE */}
						<Grid>
							{props.model.prty_title && (
								<SquareChip label={props.model.prty_title} backgroundColor={props.model.prty_color} />
							)}
						</Grid>
						{/* DATE FROM - TO */}
						<Grid>
							<DateRangeSquareChip from={props.model.proj_d_from} to={props.model.proj_d_to} />
						</Grid>
						{/* EXPECTED DURATION */}
						{props.model.proj_length_hours_planned > 0 && (
							<Grid>
								<DurationSquareChip
									id="projectsListItem.expectedDuration"
									defaultMessage={t('DURATION_EXPECTED')}
									duration={{ hours: props.model.proj_length_hours_planned }}
								/>
							</Grid>
						)}
						{/* TAGS */}
						<Grid>
							<TagsArray tags={props.model.proj_tags} changeFilter={props.changeFilter} />
						</Grid>
					</Grid>
				</Grid>
				{props.width >= 600 && (
					<Grid size={props.width >= 960 ? (canShowFinancialStats ? 3 : 4) : 6}>
						<Grid container>
							{/* INTERNAL ASSIGNED USER */}
							<Grid size={12}>
								<ButtonLink
									href={`/users/details/${props.model.proj_user_assigned_uid}`}
									icon="account-circle"
									fallback={t('VIEW_ITEM_NO_INTUSER')}
								>
									{props.model.user_assigned_title}
								</ButtonLink>
							</Grid>
							{/* EXTERNAL REFERENCE */}
							<Grid size={12}>
								<ButtonLink
									href={`/people/details/${props.model.proj_peop_uid}`}
									icon="card-account-details-outline"
									fallback={t('VIEW_ITEM_NO_EXTREF')}
								>
									{props.model.peop_title}
								</ButtonLink>
							</Grid>
						</Grid>
					</Grid>
				)}
				{props.width >= 960 && canShowFinancialStats && (
					<Grid size={3}>
						<Grid container>
							{/* PLANNED VALUE */}
							<Grid size={12}>
								<CurrencySquareChip
									id="projectsListItem.plannedValue"
									defaultMessage={t('PROJECTS_PLANNED_VALUE')}
									value={props.model.proj_value_planned}
								/>
							</Grid>
							{/* PLANNED EXPENSES */}
							<Grid size={12}>
								<CurrencySquareChip
									id="projectsListItem.plannedExpenses"
									defaultMessage={t('PROJECTS_PLANNED_EXPENSES')}
									value={props.model.proj_expenses_planned}
								/>
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>
		</GridListItem>
	);
}
